import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import { StarterKit } from "@tiptap/starter-kit"
import { TextAlign } from "@tiptap/extension-text-align"
import { Color } from "@tiptap/extension-color"
import { TextStyle } from "@tiptap/extension-text-style"
import { FontFamily } from "@tiptap/extension-font-family"
import { FontSize } from "@tiptap/extension-font-size"
import { Underline } from "@tiptap/extension-underline"
import { Link } from "@tiptap/extension-link"
import { Placeholder } from "@tiptap/extension-placeholder"

export default class extends Controller {
  static targets = ["input", "editor", "colorInput"]
  static values = { placeholder: { type: String, default: "Commencez à écrire…" } }

  connect() {
    // Vider le DOM si Turbo a restauré une page cachée (évite le double éditeur).
    this.editorTarget.innerHTML = ""

    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] }
        }),
        TextAlign.configure({
          types: ["heading", "paragraph"]
        }),
        Color,
        TextStyle,
        FontFamily,
        FontSize,
        Underline,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { class: "text-blue-600 underline", rel: "noopener", target: "_blank" }
        }),
        Placeholder.configure({
          placeholder: this.placeholderValue
        })
      ],
      content: this.inputTarget.value || "",
      parseOptions: { preserveWhitespace: "full" },
      editorProps: {
        attributes: {
          class: "tiptap-content prose max-w-none focus:outline-none min-h-[120px] p-3"
        }
      },
      onUpdate: ({ editor }) => {
        this.inputTarget.value = editor.getHTML()
      }
    })
  }

  disconnect() {
    this.editor?.destroy()
  }

  bold()      { this.editor.chain().focus().toggleBold().run() }
  italic()    { this.editor.chain().focus().toggleItalic().run() }
  underline() { this.editor.chain().focus().toggleUnderline().run() }
  strike()    { this.editor.chain().focus().toggleStrike().run() }

  heading(event) {
    const level = parseInt(event.params.level)
    this.editor.chain().focus().toggleHeading({ level }).run()
  }
  paragraph() { this.editor.chain().focus().setParagraph().run() }

  bulletList()  { this.editor.chain().focus().toggleBulletList().run() }
  orderedList() { this.editor.chain().focus().toggleOrderedList().run() }
  blockquote()  { this.editor.chain().focus().toggleBlockquote().run() }

  alignLeft()    { this.editor.chain().focus().setTextAlign("left").run() }
  alignCenter()  { this.editor.chain().focus().setTextAlign("center").run() }
  alignRight()   { this.editor.chain().focus().setTextAlign("right").run() }
  alignJustify() { this.editor.chain().focus().setTextAlign("justify").run() }

  color(event) {
    const color = event.params.color
    if (color === "reset") {
      this.editor.chain().focus().unsetColor().run()
    } else {
      this.editor.chain().focus().setColor(color).run()
    }
  }

  colorPick(event) {
    this.editor.chain().focus().setColor(event.target.value).run()
  }

  fontFamily(event) {
    const value = event.target.value
    if (!value) {
      this.editor.chain().focus().unsetFontFamily().run()
    } else {
      this.editor.chain().focus().setFontFamily(value).run()
    }
  }

  fontSize(event) {
    const value = event.target.value
    if (!value) {
      this.editor.chain().focus().unsetFontSize().run()
    } else {
      this.editor.chain().focus().setFontSize(value).run()
    }
  }

  link() {
    const previousUrl = this.editor.getAttributes("link").href
    const url = prompt("URL du lien (vide pour retirer) :", previousUrl || "https://")
    if (url === null) return
    if (url === "") {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run()
    } else {
      this.editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
    }
  }

  undo() { this.editor.chain().focus().undo().run() }
  redo() { this.editor.chain().focus().redo().run() }
}
